import { AlertTriangle, User, Building } from 'lucide-react'
import React from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { CpnBeneficiaryRequirementType, CpnTravelRule } from '@/types/cpn'

interface RequirementsDisplayCardProps {
  missingRequirements: {
    travelRule: CpnTravelRule[]
    beneficiaryAccount: CpnBeneficiaryRequirementType[]
  }
  onManageBeneficiary: () => void
  onManageUser: () => void
  isLoading?: boolean
}

const RequirementsDisplayCard: React.FC<RequirementsDisplayCardProps> = ({
  missingRequirements,
  onManageBeneficiary,
  onManageUser,
  isLoading = false,
}) => {
  // Separate originator and beneficiary requirements from travelRule
  const originatorRequirements = missingRequirements.travelRule.filter((req) =>
    req.startsWith('ORIGINATOR_')
  )

  const beneficiaryTravelRuleRequirements =
    missingRequirements.travelRule.filter((req) =>
      req.startsWith('BENEFICIARY_')
    )

  const hasMissingRequirements =
    originatorRequirements.length > 0 ||
    beneficiaryTravelRuleRequirements.length > 0 ||
    missingRequirements.beneficiaryAccount.length > 0

  const formatRequirementName = (name: string): string => {
    // Remove the prefix (ORIGINATOR_ or BENEFICIARY_) for display
    const withoutPrefix = name
      .replace(/^ORIGINATOR_/, '')
      .replace(/^BENEFICIARY_/, '')

    return withoutPrefix
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  if (!hasMissingRequirements) {
    return <></>
  }

  return (
    <Card className="p-4 bg-red-50 border-red-200 dark:bg-red-200">
      <div className="flex items-center space-x-2 mb-3">
        <AlertTriangle className="w-4 h-4 text-red-600" />
        <h3 className="font-medium text-red-900">Missing Information</h3>
      </div>

      <p className="text-sm text-red-700 mb-4">
        Some information is missing to complete this payment. Please provide the
        missing information to proceed.
      </p>

      {originatorRequirements.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <User className="w-3 h-3 text-red-600" />
            <h4 className="text-sm font-medium text-red-800">
              Sender Information
            </h4>
          </div>
          <ul className="list-disc list-inside space-y-1">
            {originatorRequirements.map((requirement, index) => (
              <li key={index} className="text-xs text-red-700">
                {formatRequirementName(requirement)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(beneficiaryTravelRuleRequirements.length > 0 ||
        missingRequirements.beneficiaryAccount.length > 0) && (
        <div className="mb-4">
          <div className="flex items-center space-x-2 mb-2">
            <Building className="w-3 h-3 text-red-600" />
            <h4 className="text-sm font-medium text-red-800">
              Recipient Information
            </h4>
          </div>
          <ul className="list-disc list-inside space-y-1">
            {beneficiaryTravelRuleRequirements.map((requirement, index) => (
              <li key={index} className="text-xs text-red-700">
                {formatRequirementName(requirement)}
              </li>
            ))}
            {missingRequirements.beneficiaryAccount.map(
              (requirement, index) => (
                <li key={index} className="text-xs text-red-700">
                  {formatRequirementName(requirement)}
                </li>
              )
            )}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {/* Show Update Personal Info button only if there are originator requirements */}
        {originatorRequirements.length > 0 && (
          <Button
            onClick={onManageUser}
            variant="outline"
            size="sm"
            className="text-red-700 border-red-300 hover:bg-red-100 hover:text-red-800"
            disabled={isLoading}
          >
            <User className="w-3 h-3 mr-1" />
            {isLoading ? 'Loading...' : 'Update Personal Info'}
          </Button>
        )}

        {/* Show Update Recipient Info button only if there are Recipient requirements */}
        {(beneficiaryTravelRuleRequirements.length > 0 ||
          missingRequirements.beneficiaryAccount.length > 0) && (
          <Button
            onClick={onManageBeneficiary}
            variant="outline"
            size="sm"
            className="text-red-700 border-red-300 hover:bg-red-100 hover:text-red-800"
            disabled={isLoading}
          >
            <Building className="w-3 h-3 mr-1" />
            {isLoading ? 'Loading...' : 'Update Recipient Info'}
          </Button>
        )}
      </div>
    </Card>
  )
}

export default RequirementsDisplayCard
